import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  buildQueryFilters,
  MAXIMUM_PAGINATION,
  useGetChildOrganizations,
  useGetOrganization,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { cn, buttonVariants, Button } from '@sib/ui';
import { MEDIA_SM_QUERY } from '@spendbrain/config/constants';
import { paths } from '@spendbrain/config/paths';
import { useMediaQuery, usePowerBiReports } from '@spendbrain/hooks';
import {
  AccountIcon,
  BillingIcon,
  CalendarIcon,
  ChartIcon,
  ChatIcon,
  DashboardIcon,
  DocumentsIcon,
  InfoIcon,
  TeamIcon,
  SavingsIcon,
  FeedbackIcon,
  LocationListIcon,
  VendorsIcon,
  YourTicketsIcon,
  QuestionMarkIcon,
  BillPayIcon,
  ContractTermsIcon,
} from '@spendbrain/icons';
import { useFeedbackStore, useSidebarStore } from '../stores';

const useSidebarItems = () => {
  const { t } = useTranslation();
  const { openFeedbackDialog } = useFeedbackStore();
  const { powerBiReports } = usePowerBiReports();
  const showReportsDirectory = powerBiReports.length > 0;
  const { organizationId } = useUserSetupStore();
  const { data: organizationData } = useGetOrganization(organizationId);
  const hasVectorClientId = !!organizationData?.vectorClientId;
  const hasNetsuiteCustomerId = !!organizationData?.netsuiteCustomerId;
  const hasBillingAccess = hasVectorClientId || hasNetsuiteCustomerId;

  const { data: childOrgsData } = useGetChildOrganizations(
    organizationId || '',
    {
      page: 0,
      size: MAXIMUM_PAGINATION,
      filter: buildQueryFilters([
        { field: 'micrositesEnabled', operator: 'eq', value: true },
      ]),
    }
  );

  const hasChildren = (childOrgsData?.totalItems ?? 0) > 0;

  return [
    ...(hasChildren
      ? [
          {
            href: paths.subsidiaryOrganization,
            title: t('sidebar.subsidiaryOrganization'),
            icon: TeamIcon,
          },
        ]
      : []),
    {
      href: paths.dashboard,
      title: t('sidebar.dashboard'),
      icon: DashboardIcon,
    },
    {
      href: paths.savings,
      title: t('sidebar.savingsAndSpendAnalysis'),
      icon: SavingsIcon,
    },
    {
      href: paths.services,
      title: t('sidebar.services'),
      icon: ChartIcon,
    },
    {
      href: paths.documents.root,
      title: t('sidebar.documents'),
      icon: DocumentsIcon,
    },
    {
      href: paths.sites,
      title: t('sidebar.sites'),
      icon: LocationListIcon,
    },

    ...(showReportsDirectory
      ? [{ href: paths.reports, title: t('sidebar.reports'), icon: ChartIcon }]
      : []),
    {
      href: paths.tickets,
      title: t('sidebar.tickets'),
      icon: YourTicketsIcon,
    },
    {
      href: paths.upcomingEvents,
      title: t('sidebar.upcomingEvents'),
      icon: CalendarIcon,
    },
    {
      href: paths.chat,
      title: t('sidebar.chat'),
      icon: ChatIcon,
    },
    {
      href: paths.yourTeam,
      title: t('sidebar.yourTeam'),
      icon: TeamIcon,
    },
    {
      href: paths.account,
      title: t('sidebar.account'),
      icon: AccountIcon,
    },
    ...(hasBillingAccess
      ? [
          {
            href: paths.billing,
            title: t('sidebar.billings'),
            icon: BillingIcon,
          },
        ]
      : []),
    {
      href: paths.vendors,
      title: t('sidebar.vendors'),
      icon: VendorsIcon,
    },
    {
      href: paths.billPay,
      title: t('sidebar.billPay'),
      icon: BillPayIcon,
    },
    {
      href: paths.contractTerms,
      title: t('sidebar.contractTerms'),
      icon: ContractTermsIcon,
    },
    {
      href: paths.help,
      title: t('sidebar.help'),
      icon: QuestionMarkIcon,
    },
    {
      href: 'https://aboutsib.com/',
      title: t('sidebar.about'),
      icon: InfoIcon,
    },
    {
      title: t('sidebar.sendFeedback'),
      icon: FeedbackIcon,
      onClick: openFeedbackDialog,
    },
  ];
};

export const NavigationMenu = () => {
  const { pathname } = useLocation();
  const { setSidebarOpen } = useSidebarStore();
  const sidebarItems = useSidebarItems();
  const currentPathname = String(pathname);
  const isMobile = useMediaQuery(MEDIA_SM_QUERY);

  return (
    <div className="group flex flex-col justify-between gap-4">
      <nav className="grid gap-1">
        {(sidebarItems || []).map(({ href, title, icon: Icon, onClick }) => {
          const isActive = href?.startsWith(currentPathname);
          const className = cn(
            buttonVariants({
              variant: 'ghost',
            }),
            'text-primary justify-start rounded-none px-[18px] font-normal no-underline',
            isActive
              ? 'bg-secondary hover:text-primary font-medium hover:brightness-100'
              : 'hover:bg-muted hover:brightness-100'
          );
          const isExternal = href?.startsWith('http');

          return onClick ? (
            <Button
              variant="ghost"
              className={className}
              key={title}
              onClick={onClick}
            >
              <Icon className="size-[18px]" />
              {title}
            </Button>
          ) : (
            <Link
              key={href}
              to={href?.toString() ?? '/#'}
              title={title}
              onClick={onClick}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
              className={className}
              {...(isMobile && {
                onClick: () => setSidebarOpen(false),
              })}
            >
              <Icon className="size-[18px]" />
              {title}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

NavigationMenu.displayName = 'NavigationMenu';
